import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
  useConnectionState,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { ConnectionState } from 'livekit-client';
import { fetchLiveKitToken, liveKitUrlCandidates } from '../../services/liveKitService';
import LiveKitCallExtras from './LiveKitCallExtras';
import CallMediaControls from './CallMediaControls';
import CallLocalPreview from './CallLocalPreview';
import CallToolsProvider, { CallToolsOverlays } from './CallToolsProvider';
import ScreenShareAnnotationLayer from './annotation/AnnotationOverlay';
import { enterCallPresence, leaveCallPresence } from '../../utils/callPresence';
import './LiveKitCallOverlay.css';

const formatRoomError = (err) => {
  const msg = String(err?.message || err || '').trim();
  if (/failed to fetch/i.test(msg) || /could not establish signal connection/i.test(msg)) {
    return 'Could not reach the call server. Port 9443 is still presenting an expired or mismatched TLS certificate.';
  }
  if (/websocket/i.test(msg) || /connection error/i.test(msg) || /failed to connect/i.test(msg)) {
    return 'Could not reach the call server. Check your network and try again.';
  }
  if (/duplicate.?identity/i.test(msg) || /already connected/i.test(msg)) {
    return 'This account is already in the call from another window. Close the other join and retry.';
  }
  return msg.replace(/^Error:\s*/i, '') || 'Could not join the call.';
};

/**
 * Replaces LiveKit's ConnectionStateToast, which flashes "Disconnected"
 * on every join before the websocket is up.
 */
const CallConnectionBanner = ({ error, onRetry }) => {
  const state = useConnectionState();
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (state === ConnectionState.Connecting || state === ConnectionState.Reconnecting) {
      setAttempted(true);
    }
  }, [state]);

  if (state === ConnectionState.Connected) return null;

  const failed = attempted && state === ConnectionState.Disconnected;
  const text = failed
    ? (error || 'Disconnected from the call.')
    : state === ConnectionState.Reconnecting
      ? 'Reconnecting…'
      : 'Connecting…';

  return (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40 max-w-[min(90vw,28rem)] px-3 py-1.5 rounded-lg bg-black/80 text-white text-xs font-medium shadow flex items-center gap-2">
      <span>{text}</span>
      {failed && (
        <button
          type="button"
          onClick={onRetry}
          className="px-2 py-0.5 rounded-md bg-white/15 hover:bg-white/25 font-semibold"
        >
          Retry
        </button>
      )}
    </div>
  );
};

/**
 * Full-screen in-app LiveKit call with camera, mic, screenshare,
 * host controls, recording, captions, and screen annotation.
 */
const LiveKitCallOverlay = ({
  channelId,
  messageId,
  roomName,
  title = 'Direct call',
  onLeave,
}) => {
  const [token, setToken] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [resolvedRoom, setResolvedRoom] = useState(roomName || '');
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [connectReady, setConnectReady] = useState(false);
  const [roomKey, setRoomKey] = useState(0);
  const [roomError, setRoomError] = useState('');
  const leftByUserRef = useRef(false);
  const urlCandidatesRef = useRef([]);
  const urlIndexRef = useRef(0);
  const failingOverRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setRoomError('');
      try {
        const data = await fetchLiveKitToken({ channelId, messageId, roomName });
        if (cancelled) return;
        if (!data?.token || !data?.url) {
          throw new Error('LiveKit token response was incomplete');
        }
        const candidates = Array.isArray(data.urls) && data.urls.length
          ? data.urls
          : liveKitUrlCandidates(data.url);
        urlCandidatesRef.current = candidates;
        urlIndexRef.current = 0;
        failingOverRef.current = false;
        setToken(data.token);
        setServerUrl(candidates[0] || data.url);
        setResolvedRoom(data.roomName || roomName || '');
        setIsOrganizer(Boolean(data.organizer));
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.error
          || err?.response?.data
          || err?.message
          || 'Failed to join call';
        setError(typeof msg === 'string' ? msg.replace(/^Error:\s*/i, '') : 'Failed to join call');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [channelId, messageId, roomName, roomKey]);

  // Delay connect past React StrictMode's phantom unmount so the same
  // identity is not kicked as a duplicate (that surfaces as Disconnected).
  useEffect(() => {
    if (!token || !serverUrl) {
      setConnectReady(false);
      return undefined;
    }
    setConnectReady(false);
    const t = window.setTimeout(() => setConnectReady(true), 80);
    return () => {
      window.clearTimeout(t);
      setConnectReady(false);
    };
  }, [token, serverUrl, roomKey]);

  useEffect(() => {
    enterCallPresence({
      title,
      channelId,
      messageId,
      roomName: roomName || resolvedRoom,
    });
    const endPresence = () => leaveCallPresence({ immediate: true, keepalive: true });
    window.addEventListener('pagehide', endPresence);
    window.addEventListener('beforeunload', endPresence);
    return () => {
      window.removeEventListener('pagehide', endPresence);
      window.removeEventListener('beforeunload', endPresence);
      leaveCallPresence();
    };
  }, []);

  const handleLeave = useCallback(() => {
    leftByUserRef.current = true;
    onLeave?.();
  }, [onLeave]);

  const tryNextSignalUrl = useCallback(() => {
    if (leftByUserRef.current || failingOverRef.current) return false;
    const next = urlIndexRef.current + 1;
    const candidates = urlCandidatesRef.current;
    if (next >= candidates.length) return false;
    failingOverRef.current = true;
    urlIndexRef.current = next;
    setRoomError('');
    setServerUrl(candidates[next]);
    window.setTimeout(() => {
      failingOverRef.current = false;
    }, 400);
    return true;
  }, []);

  const handleRetry = useCallback(() => {
    setRoomError('');
    urlIndexRef.current = 0;
    failingOverRef.current = false;
    setRoomKey((n) => n + 1);
  }, []);

  const handleDisconnected = useCallback(() => {
    if (leftByUserRef.current) return;
    if (tryNextSignalUrl()) return;
  }, [tryNextSignalUrl]);

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col bg-gray-950 text-white"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/40 flex-shrink-0">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{title}</p>
          <p className="text-[11px] text-white/60">
            WorkLoop Direct Call
            {isOrganizer ? ' · organizer' : ''}
            {' · camera, mic, screenshare, captions'}
          </p>
        </div>
        <button
          type="button"
          onClick={handleLeave}
          className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-red-600 hover:bg-red-500"
        >
          Leave
        </button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col lk-workloop-call relative" data-lk-theme="default">
        {loading && (
          <div className="flex-1 flex items-center justify-center text-sm text-white/70">
            Connecting…
          </div>
        )}
        {!loading && error && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-sm text-red-300 max-w-md">{error}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleRetry}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-white/15 hover:bg-white/25"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={handleLeave}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-white/10 hover:bg-white/20"
              >
                Close
              </button>
            </div>
          </div>
        )}
        {!loading && !error && token && serverUrl && (
          <LiveKitRoom
            key={`${roomKey}:${serverUrl}`}
            token={token}
            serverUrl={serverUrl}
            connect={connectReady}
            video={false}
            audio={false}
            onDisconnected={handleDisconnected}
            onError={(err) => {
              console.warn('LiveKit room error', err);
              if (leftByUserRef.current) return;
              if (tryNextSignalUrl()) return;
              setRoomError(formatRoomError(err));
            }}
            onMediaDeviceFailure={(failure, kind) => {
              console.warn('LiveKit media device failure', kind, failure);
            }}
            className="flex flex-col flex-1 min-h-0 h-full overflow-hidden"
            style={{ height: '100%' }}
            options={{
              adaptiveStream: true,
              dynacast: true,
            }}
            connectOptions={{
              autoSubscribe: true,
              maxRetries: 5,
              peerConnectionTimeout: 45000,
            }}
          >
            <CallToolsProvider>
            <CallConnectionBanner error={roomError} onRetry={handleRetry} />
            <LiveKitCallExtras
              channelId={channelId}
              messageId={messageId}
              roomName={resolvedRoom}
              isOrganizer={isOrganizer}
            />
            <div className="relative flex-1 min-h-0 overflow-hidden">
              <VideoConference className="h-full min-h-0" />
              <ScreenShareAnnotationLayer />
              <CallLocalPreview />
              <CallToolsOverlays />
            </div>
            <CallMediaControls />
            <RoomAudioRenderer />
            </CallToolsProvider>
          </LiveKitRoom>
        )}
      </div>
    </div>
  );
};

export default LiveKitCallOverlay;
